import type { IncomingHttpHeaders } from 'http';
import type { ParsedUrlQuery } from 'querystring';
import { useContext } from 'react';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/router';

import type { Locale } from '@packages/config';
import { TrackingContext } from '@packages/tracking';
import { useUserAgentDevice } from '@packages/shared';
import type { SearchParametersInput } from '@packages/gql/generated/shopping/graphql';
import type { Device } from '@packages/themes/src/default';
import { useCookies } from '@packages/utilities';

import {
  decodeCategoryFilter,
  decodeSelectedFilterValues,
  decodeUserInteraction,
} from '../queryEncoding';
import { getChannelFromDevice } from '../hooks/useChannel';
import { PERSONALIZATION_COOKIE_NAME } from '../hooks/usePersonalizationCookie';
import { getNitroIdFromCookies } from './getNitroIdFromCookies';

const prepareHighlightArticles = (commaSeparatedArticleNumbers: string) =>
  commaSeparatedArticleNumbers
    .split(',')
    .filter((x) => x)
    .map((articleNumber) => ({ articleNumber }));

const channelMapping = {
  web: 'DESKTOP',
  tab: 'TABLET',
  mob: 'MOBILE',
} as const;

const buildGqlParams = (
  query: ParsedUrlQuery,
  locale: Locale,
  device: Device,
  isBot: boolean,
  cookies: Record<string, string>,
): SearchParametersInput => {
  const personalizationString = cookies[PERSONALIZATION_COOKIE_NAME];
  const personalizationTokens = personalizationString?.split(',') ?? [];
  const personalizationSegment = personalizationTokens[personalizationTokens.length - 1];

  const visitorId = getNitroIdFromCookies(cookies);

  const userInteraction = decodeUserInteraction(query);
  const categoryId = decodeCategoryFilter(query);
  const highlightArticles =
    typeof query.highlight === 'string' ? prepareHighlightArticles(query.highlight) : [];

  return {
    environment: {
      locale,
      channel: channelMapping[getChannelFromDevice(device)],
      isBot,
    },
    version: '22',
    constraints: {
      appliedFilters: Object.entries(decodeSelectedFilterValues(query)).map(
        ([filterId, values]) => ({
          filterId,
          values,
        }),
      ),
      ...(categoryId && { categoryId }),
    },
    ...(highlightArticles.length && {
      overrides: { highlightArticles },
    }),
    tracking: {
      ...(personalizationSegment && { personalizationSegment }),
      ...(visitorId && { visitorId }),
      ...(userInteraction && { userInteraction }),
      ...(typeof query.lmPromo === 'string' && { promotionId: query.lmPromo }),
    },
  };
};

export const getServerSideGqlParams = (
  query: ParsedUrlQuery,
  locale: Locale,
  headers: IncomingHttpHeaders,
  cookies: Record<string, string>,
) => {
  const device = headers?.['x-ua-device'] as Device;
  const isBot = headers?.['x-ua-bot'] !== undefined;

  return buildGqlParams(query, locale as Locale, device, isBot, cookies);
};

export const useClientSideGqlParams = () => {
  const { query } = useRouter();
  const { locale } = useIntl();

  const { isBot } = useContext(TrackingContext) ?? { isBot: false };
  const device = useUserAgentDevice();
  const { getCookies } = useCookies();
  const cookies = getCookies();

  return buildGqlParams(query, locale as Locale, device, isBot, cookies);
};
